import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { 
  Building, MapPin, Phone, Mail, Globe, Shield, Star, CheckCircle,
  Package, MessageSquare, Calendar, Users, TrendingUp, Award, ChevronRight, Clock, Lock
} from 'lucide-react';
import { suppliersApi, inquiriesApi, reviewsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { isContactLocked } from '../utils/membershipUtils';

const SupplierProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryData, setInquiryData] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        const { data } = await suppliersApi.getById(userId);
        setSupplier(data);
        try {
          const reviewsRes = await reviewsApi.getBySupplier(userId);
          setReviews(reviewsRes.data.reviews || []);
        } catch {}
      } catch (error) {
        console.error('Error fetching supplier:', error);
        toast.error('Supplier not found');
        navigate('/suppliers');
      } finally {
        setLoading(false);
      }
    };
    fetchSupplier();
  }, [userId, navigate]);

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/suppliers/${userId}` } } });
      return;
    }

    setSending(true);
    try {
      await inquiriesApi.create({
        supplier_id: userId,
        subject: inquiryData.subject || 'Product Inquiry',
        message: inquiryData.message
      });
      toast.success('Inquiry sent successfully!');
      setInquiryOpen(false);
      setInquiryData({ subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send inquiry');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="animate-pulse">
          <div className="h-48 bg-slate-200"></div>
          <div className="container-main py-8">
            <div className="h-8 bg-slate-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-slate-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!supplier) return null;

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) { navigate('/login'); return; }
    setSubmittingReview(true);
    try {
      await reviewsApi.create({ supplier_id: userId, ...reviewForm });
      toast.success('Review submitted!');
      setReviewForm({ rating: 5, title: '', comment: '' });
      const reviewsRes = await reviewsApi.getBySupplier(userId);
      setReviews(reviewsRes.data.reviews || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      {/* Banner */}
      <div className="h-48 bg-gradient-to-r from-slate-900 to-slate-700 relative">
        {supplier.banner_url && (
          <img src={supplier.banner_url} alt="" className="w-full h-full object-cover opacity-50" />
        )}
      </div>

      <div className="container-main -mt-16 relative z-10">
        {/* Profile Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#EA580C] flex items-center justify-center text-white text-3xl font-bold flex-shrink-0 shadow-lg">
              {supplier.company_name?.charAt(0) || 'S'}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-slate-900" data-testid="supplier-name">
                  {supplier.company_name}
                </h1>
                {supplier.is_verified && (
                  <Badge className="badge-verified">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
                {supplier.membership && supplier.membership !== 'free' && (
                  <Badge className="badge-gold">
                    <Star className="w-3 h-3 mr-1" />
                    {supplier.membership.toUpperCase()}
                  </Badge>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-4">
                {supplier.country && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {supplier.city ? `${supplier.city}, ` : ''}{supplier.country}
                  </span>
                )}
                {supplier.established_year && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Est. {supplier.established_year}
                  </span>
                )}
                {supplier.employees && (
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {supplier.employees} employees
                  </span>
                )}
                {supplier.created_at && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Member since {new Date(supplier.created_at).getFullYear()}
                  </span>
                )}
              </div>

              {supplier.company_intro && (
                <p className="text-slate-600 line-clamp-3">{supplier.company_intro}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {isContactLocked(user) ? (
                <Button
                  onClick={() => navigate('/membership')}
                  className="bg-[#EA580C] hover:bg-[#C2410C]"
                  data-testid="supplier-contact-locked-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Upgrade to Contact
                </Button>
              ) : (
              <Dialog open={inquiryOpen} onOpenChange={setInquiryOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#EA580C] hover:bg-[#C2410C]" data-testid="supplier-contact-btn">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Contact Supplier
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Contact Supplier</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleInquiry} className="space-y-4 mt-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Subject</label>
                      <Input
                        value={inquiryData.subject}
                        onChange={(e) => setInquiryData({ ...inquiryData, subject: e.target.value })}
                        placeholder="Subject of your inquiry"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Message *</label>
                      <Textarea
                        value={inquiryData.message}
                        onChange={(e) => setInquiryData({ ...inquiryData, message: e.target.value })}
                        placeholder="Describe your requirements..."
                        rows={4}
                        required
                        className="mt-1"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={sending}>
                      {sending ? 'Sending...' : 'Send Inquiry'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              )}
              
              {supplier.whatsapp && !isContactLocked(user) && (
                <Button variant="outline" asChild>
                  <a href={`https://wa.me/${supplier.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                    <Phone className="w-4 h-4 mr-2" />
                    WhatsApp
                  </a>
                </Button>
              )}
              {supplier.whatsapp && isContactLocked(user) && (
                <Button variant="outline" onClick={() => navigate('/membership')}>
                  <Lock className="w-4 h-4 mr-2" />
                  WhatsApp (Upgrade)
                </Button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{supplier.stats?.products || 0}</p>
              <p className="text-sm text-slate-500">Products</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{supplier.stats?.inquiries || 0}</p>
              <p className="text-sm text-slate-500">Inquiries</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#16A34A]">{supplier.stats?.response_rate || 0}%</p>
              <p className="text-sm text-slate-500">Response Rate</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{supplier.stats?.views || 0}</p>
              <p className="text-sm text-slate-500">Profile Views</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="mb-8">
          <TabsList className="bg-white border">
            <TabsTrigger value="products" data-testid="supplier-tab-products">Products</TabsTrigger>
            <TabsTrigger value="about" data-testid="supplier-tab-about">About</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="supplier-tab-reviews">Reviews ({reviews.length})</TabsTrigger>
            <TabsTrigger value="certifications" data-testid="supplier-tab-certs">Certifications</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-6">
            {supplier.products?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {supplier.products.map((product) => (
                  <Link
                    key={product.product_id}
                    to={`/products/${product.product_id}`}
                    className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="h-40 bg-slate-100">
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt={product.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-10 h-10 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-slate-900 line-clamp-2 mb-2">{product.title}</h3>
                      <span className="text-[#EA580C] font-semibold">
                        ${product.price_min?.toFixed(2)} - ${product.price_max?.toFixed(2)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No products listed yet</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="about" className="mt-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
              {supplier.company_intro && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Company Introduction</h3>
                  <p className="text-slate-600">{supplier.company_intro}</p>
                </div>
              )}

              {supplier.main_products?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Main Products</h3>
                  <div className="flex flex-wrap gap-2">
                    {supplier.main_products.map((p, i) => (
                      <Badge key={i} variant="outline">{p}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {supplier.export_countries?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Export Countries</h3>
                  <p className="text-slate-600">{supplier.export_countries.join(', ')}</p>
                </div>
              )}

              {supplier.quality_control && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Quality Control</h3>
                  <p className="text-slate-600">{supplier.quality_control}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                {supplier.website && (
                  <div>
                    <p className="text-sm text-slate-500">Website</p>
                    {isContactLocked(user) ? (
                      <p className="text-slate-400 text-sm flex items-center gap-1"><Lock className="w-3 h-3" /> Upgrade to view</p>
                    ) : (
                      <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline">
                        {supplier.website}
                      </a>
                    )}
                  </div>
                )}
                {supplier.phone && (
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    {isContactLocked(user) ? (
                      <p className="text-slate-400 text-sm flex items-center gap-1"><Lock className="w-3 h-3" /> Upgrade to view</p>
                    ) : (
                      <p className="text-slate-900">{supplier.phone}</p>
                    )}
                  </div>
                )}
                {supplier.address && (
                  <div>
                    <p className="text-sm text-slate-500">Address</p>
                    {isContactLocked(user) ? (
                      <p className="text-slate-400 text-sm flex items-center gap-1"><Lock className="w-3 h-3" /> Upgrade to view</p>
                    ) : (
                      <p className="text-slate-900">{supplier.address}</p>
                    )}
                  </div>
                )}
                {supplier.annual_revenue && (
                  <div>
                    <p className="text-sm text-slate-500">Annual Revenue</p>
                    <p className="text-slate-900">{supplier.annual_revenue}</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <div className="space-y-6">
              {/* Average Rating */}
              {supplier.avg_rating > 0 && (
                <div className="bg-white border border-slate-200 p-6 flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-[#0A192F]">{supplier.avg_rating?.toFixed(1)}</p>
                    <div className="flex gap-0.5 mt-1">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`w-4 h-4 ${s <= Math.round(supplier.avg_rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{supplier.review_count || reviews.length} reviews</p>
                  </div>
                </div>
              )}

              {/* Reviews List */}
              {reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="bg-white border border-slate-200 p-5">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-[#0A192F] text-sm">{review.reviewer_name}</p>
                          <p className="text-xs text-slate-400">{review.reviewer_country} · {new Date(review.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-3.5 h-3.5 ${s <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                          ))}
                        </div>
                      </div>
                      {review.title && <h4 className="font-semibold text-sm text-[#0A192F] mb-1">{review.title}</h4>}
                      <p className="text-sm text-slate-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white border border-slate-200 p-8 text-center">
                  <Star className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No reviews yet</p>
                </div>
              )}

              {/* Write Review Form */}
              {isAuthenticated && user?.user_type === 'buyer' && (
                <div className="bg-white border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#0A192F] mb-4">Write a Review</h3>
                  <form onSubmit={handleReviewSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Rating</label>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(s => (
                          <button key={s} type="button" onClick={() => setReviewForm({...reviewForm, rating: s})} className="p-0.5">
                            <Star className={`w-6 h-6 cursor-pointer ${s <= reviewForm.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Title</label>
                      <Input value={reviewForm.title} onChange={(e) => setReviewForm({...reviewForm, title: e.target.value})} placeholder="Brief summary" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Comment *</label>
                      <Textarea value={reviewForm.comment} onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})} placeholder="Share your experience..." rows={3} required />
                    </div>
                    <Button type="submit" className="bg-[#0A192F] hover:bg-[#172A46] rounded-sm" disabled={submittingReview} data-testid="submit-review-btn">
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="certifications" className="mt-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              {supplier.certifications?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {supplier.certifications.map((cert, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                      <Award className="w-8 h-8 text-[#2563EB]" />
                      <span className="font-medium text-slate-900">{cert}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No certifications listed</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default SupplierProfilePage;
